// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://${string}` | (string & {});
};

/**
 * Address Information
 */
export type AddressInfo = {
    /**
     * Street address, P.O. Box, company name, c/o
     */
    addressLineOne: string;
    /**
     * Apartment, suite, unit, building, floor, etc.
     */
    addressLineTwo?: string;
    city: string;
    /**
     * A field for 2-letter ISO country codes
     */
    country: string;
    stateProvinceRegion: string;
    zipPostalCode: string;
};

/**
 * Values that can be used to suggest addresses
 */
export type AddressSuggestPostBody = {
    /**
     * Street address, P.O. Box, company name, c/o
     */
    addressLineOne?: string | null;
    /**
     * Apartment, suite, unit, building, floor, etc.
     */
    addressLineTwo?: string | null;
    city?: string | null;
    /**
     * A field for 2-letter ISO country codes
     */
    country?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    name?: string | null;
    stateProvinceRegion?: string | null;
    zipPostalCode?: string | null;
};

/**
 * Address suggestion item
 */
export type AddressSuggestionItem = {
    addressLineOne?: string | null;
    addressLineTwo?: string | null;
    city?: string | null;
    country?: string | null;
    name?: string | null;
    stateProvinceRegion?: string | null;
    zipPostalCode?: string | null;
};

/**
 * A list of autocomplete addresses
 */
export type AddressSuggestionListBody = {
    /**
     * an array of addresses
     */
    items: Array<AddressSuggestionItem>;
};

/**
 * Business partner Properties
 */
export type BusinessPartnerBody = {
    companyAddress: AddressInfo;
    /**
     * Business partner created at timestamp
     */
    createdAt: string;
    extId: CharacterStringUrn;
    id: CharacterStringUuid;
    /**
     * Business partner last modified at timestamp
     */
    modifiedAt?: string;
    name: CharacterString255;
    ownerId?: CharacterStringUuid;
    parentId?: CharacterStringUuid;
    type: CharacterStringType;
};

/**
 * Business partner change request properties
 */
export type BusinessPartnerChangeBody = {
    companyAddress: AddressInfo;
    name: CharacterString255;
};

/**
 * A list of businessPartnerBody
 */
export type BusinessPartnerListBody = {
    /**
     * an array of businessPartnerBody
     */
    items: Array<BusinessPartnerBody>;
};

/**
 * Change ownership accept body
 */
export type ChangeOwnershipRequestAcceptBody = {
    businessPartner: BusinessPartnerBody;
    id: CharacterStringUuid;
    status: ChangeOwnershipRequestStatusEnum;
};

/**
 * Change ownership body
 */
export type ChangeOwnershipRequestBody = {
    id: CharacterStringUuid;
    status: ChangeOwnershipRequestStatusEnum;
};

/**
 * Change ownership post body
 */
export type ChangeOwnershipRequestPostBody = {
    requestedOwnerEmail: CharacterStringEmail;
};

/**
 * Change ownership request status
 */
export enum ChangeOwnershipRequestStatusEnum {
    PENDING = 'pending',
    ACCEPTED = 'accepted',
    DONE = 'done'
}

/**
 * string of at most 255 characters
 */
export type CharacterString255 = string;

/**
 * string of at most 64 characters
 */
export type CharacterString64 = string;

export type CharacterStringEmail = string;

export type CharacterStringType = string;

export type CharacterStringUrn = string;

/**
 * a Universally Unique IDentifier in conformance with IETF RFC 4122
 */
export type CharacterStringUuid = string;

/**
 * ISO 4217 currency code
 */
export type CurrencyCode = string;

/**
 * Customer
 */
export type CustomerBody = {
    companyAddress: AddressInfo;
    id: CharacterStringUuid;
    name: CharacterString255;
    phone: CharacterString255;
};

/**
 * A list of customers
 */
export type CustomerListBody = {
    /**
     * an array of customers
     */
    items: Array<CustomerBody>;
};

/**
 * Request body for the migration candidate accept endpoint. externalId identifies the target Business Partner by ext_id (Topcon Resource Name, e.g. urn:topcon:md::000000000001:bp).
 */
export type MigrationCandidateAcceptPostBody = {
    /**
     * The ext_id (Topcon Resource Name) of the Business Partner to link this migration candidate to.
     */
    externalId: string;
};

/**
 * Migration candidate business partner properties
 */
export type MigrationCandidateBusinessPartnerBody = {
    address: AddressInfo;
    businessPartner?: BusinessPartnerBody;
    /**
     * Migration candidate created at timestamp
     */
    createdAt: string;
    dealerBusinessPartnerId?: CharacterStringUuid;
    id: CharacterStringUuid;
    matchDetails?: MigrationCandidateMatchDetails;
    /**
     * Migration candidate last modified at timestamp
     */
    modifiedAt: string;
    name: CharacterString255;
    /**
     * Status of the migration candidate's service agreement (pending, accepted, rejected, or matched). When status is accepted or matched, businessPartner is not null, and serviceAgreementStatus is pending then a service agreement is needed. matched and accepted are terminal states.
     */
    serviceAgreementStatus: 'pending' | 'accepted' | 'rejected' | 'matched';
    sourceId: CharacterString255;
    sourceSystem: CharacterString64;
    /**
     * Status of migration candidate (pending, accepted, rejected or matched).  matched and accepted are terminal states.
     */
    status: 'pending' | 'accepted' | 'rejected' | 'matched';
};

/**
 * A list of migrationCandidateBusinessPartnerBody
 */
export type MigrationCandidateBusinessPartnerListBody = {
    /**
     * an array of migrationCandidateBusinessPartnerBody
     */
    items: Array<MigrationCandidateBusinessPartnerBody>;
};

/**
 * Match-quality metadata produced by a source system's matching algorithm. The known fields mirror the TLM enricher output (project/migration/tlm/ciamstore); because this is stored as a JSONB column and the shape may vary by source system, additional fields may be present.
 */
export type MigrationCandidateMatchDetails = {
    /**
     * Overall score of the best (first) match target.
     */
    score: number;
    /**
     * Score gap between the first and second match targets (NULL when fewer than two targets).
     */
    score_gap_1_2: number | null;
    /**
     * Ranked list of candidate match targets (may be empty).
     */
    top_targets: Array<MigrationCandidateMatchTarget>;
};

/**
 * A candidate match target considered by the source system's matching algorithm, with its per-dimension score breakdown.
 */
export type MigrationCandidateMatchTarget = {
    /**
     * Address-similarity component of the score.
     */
    address_score: number;
    /**
     * Match target city (may be empty if unparseable).
     */
    business_address_city?: string | null;
    /**
     * Match target ISO country code (may be empty if unparseable).
     */
    business_address_country_code?: string | null;
    /**
     * Match target street address line 1 (may be empty if unparseable).
     */
    business_address_line_1?: string | null;
    /**
     * Match target state/province/region (may be empty if unparseable).
     */
    business_address_state_province_region?: string | null;
    /**
     * Match target ZIP/postal code (may be empty if unparseable).
     */
    business_address_zip_postal_code?: string | null;
    /**
     * Domain-similarity component of the score.
     */
    domain_score: number;
    /**
     * External identifier of the match target (e.g. a Topcon Resource Name).
     */
    external_id: string;
    /**
     * Account name of the match target.
     */
    name: string;
    /**
     * Name-similarity component of the score.
     */
    name_score: number;
    /**
     * Overall match score for this target.
     */
    score: number;
};

/**
 * Migration candidate user properties
 */
export type MigrationCandidateUserBody = {
    /**
     * Migration candidate user created at timestamp
     */
    createdAt: string;
    email: CharacterStringEmail;
    id: CharacterStringUuid;
    /**
     * Migration candidate user last modified at timestamp
     */
    modifiedAt: string;
    /**
     * Array of profile suffixes for the user (e.g., company.administrator)
     */
    profiles: Array<string>;
    /**
     * Status of migration candidate user (pending, accepted, rejected)
     */
    status: 'pending' | 'accepted' | 'rejected';
};

/**
 * Pending migration candidate users whose business partner candidate is matched or accepted to the current business partner
 */
export type MigrationCandidateUserListBody = {
    /**
     * an array of migrationCandidateUserBody
     */
    items: Array<MigrationCandidateUserBody>;
};

/**
 * A paginated list of businessPartnerBody
 */
export type PaginatedBusinessPartnerListBody = {
    /**
     * an array of businessPartnerBody
     */
    items: Array<BusinessPartnerBody>;
    /**
     * current limit query value
     */
    limit: number;
    /**
     * current page query value
     */
    page: number;
    /**
     * total count
     */
    total: number;
};

/**
 * A paginated list of migrationCandidateBusinessPartnerBody
 */
export type PaginatedMigrationCandidateBusinessPartnerListBody = {
    /**
     * an array of migrationCandidateBusinessPartnerBody
     */
    items: Array<MigrationCandidateBusinessPartnerBody>;
    /**
     * current limit query value
     */
    limit: number;
    /**
     * current page query value
     */
    page: number;
    /**
     * total count
     */
    total: number;
};

/**
 * A paginated list of requestBusinessPartnerBody
 */
export type PaginatedRequestBusinessPartnerListBody = {
    /**
     * an array of requestBusinessPartnerBody
     */
    items: Array<RequestBusinessPartnerBody>;
    /**
     * current limit query value
     */
    limit: number;
    /**
     * current page query value
     */
    page: number;
    /**
     * total count
     */
    total: number;
};

/**
 * A paginated list of serviceAgreementBody
 */
export type PaginatedServiceAgreementListBody = {
    /**
     * an array of serviceAgreementBody
     */
    items: Array<ServiceAgreementBody>;
    /**
     * current limit query value
     */
    limit: number;
    /**
     * current page query value
     */
    page: number;
    /**
     * total count
     */
    total: number;
};

/**
 * Product line
 */
export type ProductLineBody = {
    id?: CharacterStringUuid;
    name?: CharacterString255;
};

/**
 * A list of product lines
 */
export type ProductLineListBody = {
    /**
     * an array of product lines
     */
    items: Array<ProductLineBody>;
};

/**
 * service provider
 */
export type ProviderBody = {
    companyAddress: AddressInfo;
    id: CharacterStringUuid;
    name: CharacterString255;
};

/**
 * A paginated list of service providers
 */
export type ProviderListBody = {
    /**
     * an array of service providers
     */
    items: Array<ProviderBody>;
    /**
     * current limit query value
     */
    limit: number;
    /**
     * current page query value
     */
    page: number;
    /**
     * total count
     */
    total: number;
};

/**
 * Registration token response
 */
export type RegistrationTokenBody = {
    /**
     * An HMAC-signed JWT containing the owner's email and a nonce, for use as the register parameter in the PingFederate registration invite flow
     */
    registrationToken: string;
};

/**
 * Request to create a business partner
 */
export type RequestBusinessPartnerBody = {
    companyAddress: AddressInfo;
    /**
     * Business partner request created at timestamp
     */
    createdAt: string;
    currency: CurrencyCode;
    /**
     * Customer review expiration timestamp
     */
    expiresAt?: string;
    id: CharacterStringUuid;
    /**
     * Business partner request last modified at timestamp
     */
    modifiedAt: string;
    /**
     * Requested end customer or division name
     */
    name: string;
    ownerIdentityId: CharacterStringUuid;
    parentBusinessPartnerId?: CharacterStringUuid;
    provider: ProviderBody;
    providerBusinessPartnerId: CharacterStringUuid;
    providerIdentityId?: CharacterStringUuid;
    serviceAgreementTerms: ServiceAgreementTermsEnum;
    status: RequestBusinessPartnerStatusEnum;
    type: CharacterStringType;
};

/**
 * Customer declines business partner request
 */
export type RequestBusinessPartnerDeclinePostBody = {
    declineReason?: RequestBusinessPartnerDeclineReasonEnum;
};

/**
 * Business partner request decline reason
 */
export enum RequestBusinessPartnerDeclineReasonEnum {
    REASON_NONE = 'reason.none',
    REASON_OTHER = 'reason.other',
    REASON_CUSTOMER_INVALID_DETAILS = 'reason.customer.invalid_details',
    REASON_SERVICE_AGREEMENT_DETAILS_NOT_SATISFACTORY = 'reason.service_agreement.details_not_satisfactory'
}

/**
 * Request to create an end-customer type of business partner division
 */
export type RequestBusinessPartnerDivisionPostBody = {
    companyAddress: AddressInfo;
    currency: CurrencyCode;
    /**
     * Requested division name
     */
    name: string;
    providerBusinessPartnerId: CharacterStringUuid;
    serviceAgreementTerms: ServiceAgreementTermsEnum;
    type: RequestBusinessPartnerEndCustomerTypeEnum;
};

/**
 * Request to create an end-customer type of business partner
 */
export type RequestBusinessPartnerEndCustomerPostBody = {
    companyAddress: AddressInfo;
    currency: CurrencyCode;
    migrationCandidateBusinessPartnerId?: CharacterStringUuid;
    /**
     * Requested end customer name
     */
    name: string;
    providerBusinessPartnerId: CharacterStringUuid;
    serviceAgreementTerms: ServiceAgreementTermsEnum;
    type: RequestBusinessPartnerEndCustomerTypeEnum;
};

/**
 * Business partner request end-customer type
 */
export enum RequestBusinessPartnerEndCustomerTypeEnum {
    END_CUSTOMER_ENTERPRISE = 'end_customer_enterprise',
    END_CUSTOMER_INDIVIDUAL = 'end_customer_individual'
}

/**
 * A list of requestBusinessPartnerBody
 */
export type RequestBusinessPartnerListBody = {
    /**
     * an array of requestBusinessPartnerBody
     */
    items: Array<RequestBusinessPartnerBody>;
};

/**
 * Business partner request patch properties
 */
export type RequestBusinessPartnerPatchBody = {
    companyAddress: AddressInfo;
    /**
     * Requested end customer or division name
     */
    name: string;
};

/**
 * As a service provider, proxy a request to create an end-customer type of business partner
 */
export type RequestBusinessPartnerProxyPostBody = {
    companyAddress: AddressInfo;
    currency: CurrencyCode;
    migrationCandidateBusinessPartnerId?: CharacterStringUuid;
    /**
     * Requested end customer name
     */
    name: string;
    ownerEmail: CharacterStringEmail;
    providerCustomerNumber?: CharacterString64;
    serviceAgreementTerms: ServiceAgreementTermsEnum;
    type: RequestBusinessPartnerEndCustomerTypeEnum;
};

/**
 * Business partner request status
 */
export enum RequestBusinessPartnerStatusEnum {
    CUSTOMER_REVIEW = 'customer_review',
    CUSTOMER_REJECTED = 'customer_rejected',
    PENDING = 'pending',
    ACCEPTED = 'accepted',
    REJECTED = 'rejected',
    DONE = 'done',
    CLOSED = 'closed'
}

/**
 * A list of role
 */
export type RoleListBody = {
    /**
     * an array of role string
     */
    items: Array<string>;
};

/**
 * Service agreement properties
 */
export type ServiceAgreementBody = {
    /**
     * Active service agreement start timestamp
     */
    activeSince?: string;
    /**
     * Service agreement created at timestamp
     */
    createdAt: string;
    customer?: BusinessPartnerBody;
    customerBusinessPartnerId: CharacterStringUuid;
    declineReason?: ServiceAgreementDeclineReasonEnum;
    id: CharacterStringUuid;
    /**
     * Service agreement last modified at timestamp
     */
    modifiedAt: string;
    provider?: BusinessPartnerBody;
    providerBusinessPartnerId: CharacterStringUuid;
    providerCustomerNumber?: CharacterString64;
    /**
     * Pending service agreement expiration timestamp
     */
    requestExpiresAt?: string;
    serviceAgreementStatus: ServiceAgreementStatusEnum;
    serviceAgreementTerms: ServiceAgreementTermsEnum;
};

/**
 * Service agreement invitee declines service agreement request
 */
export type ServiceAgreementDeclinePostBody = {
    declineReason?: ServiceAgreementDeclineReasonEnum;
};

/**
 * Service agreement decline reason
 */
export enum ServiceAgreementDeclineReasonEnum {
    REASON_NONE = 'reason.none',
    REASON_OTHER = 'reason.other',
    REASON_CUSTOMER_INVALID_DETAILS = 'reason.customer.invalid_details',
    REASON_SERVICE_AGREEMENT_DETAILS_NOT_SATISFACTORY = 'reason.service_agreement.details_not_satisfactory'
}

/**
 * A list of serviceAgreementBody
 */
export type ServiceAgreementListBody = {
    /**
     * an array of serviceAgreementBody
     */
    items: Array<ServiceAgreementBody>;
};

/**
 * Customer or provider updates service agreement
 */
export type ServiceAgreementPatchBody = {
    serviceAgreementTerms: ServiceAgreementTermsEnum;
};

/**
 * Service agreement status
 */
export enum ServiceAgreementStatusEnum {
    ACTIVE = 'active',
    DECLINED = 'declined',
    INVITED = 'invited',
    PENDING = 'pending'
}

/**
 * Service agreement terms type
 */
export enum ServiceAgreementTermsEnum {
    FULL_SERVICE = 'full_service',
    PARTIAL_SERVICE = 'partial_service'
}

/**
 * Provider creates a service agreement with a customer
 */
export type ServiceAgreementWithCustomerPostBody = {
    customerBusinessPartnerId: CharacterStringUuid;
    customerNumber?: CharacterString64;
    serviceAgreementTerms: ServiceAgreementTermsEnum;
};

/**
 * Customer creates a service agreement with a provider
 */
export type ServiceAgreementWithProviderPostBody = {
    providerBusinessPartnerId: CharacterStringUuid;
    serviceAgreementTerms: ServiceAgreementTermsEnum;
};

export type PostV1AddressSuggestData = {
    /**
     * TODO
     */
    body: AddressSuggestPostBody;
    path?: never;
    query?: never;
    url: '/v1/address/suggest';
};

export type PostV1AddressSuggestErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type PostV1AddressSuggestResponses = {
    /**
     * Successful Response
     */
    200: AddressSuggestionListBody;
};

export type PostV1AddressSuggestResponse = PostV1AddressSuggestResponses[keyof PostV1AddressSuggestResponses];

export type GetV1BusinessPartnerCurrentData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current';
};

export type GetV1BusinessPartnerCurrentErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1BusinessPartnerCurrentResponses = {
    /**
     * Successful Response
     */
    200: BusinessPartnerBody;
};

export type GetV1BusinessPartnerCurrentResponse = GetV1BusinessPartnerCurrentResponses[keyof GetV1BusinessPartnerCurrentResponses];

export type DeleteV1BusinessPartnerCurrentChangeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/change';
};

export type DeleteV1BusinessPartnerCurrentChangeErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type DeleteV1BusinessPartnerCurrentChangeResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteV1BusinessPartnerCurrentChangeResponse = DeleteV1BusinessPartnerCurrentChangeResponses[keyof DeleteV1BusinessPartnerCurrentChangeResponses];

export type GetV1BusinessPartnerCurrentChangeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/change';
};

export type GetV1BusinessPartnerCurrentChangeErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1BusinessPartnerCurrentChangeResponses = {
    /**
     * Successful Response
     */
    200: BusinessPartnerChangeBody;
};

export type GetV1BusinessPartnerCurrentChangeResponse = GetV1BusinessPartnerCurrentChangeResponses[keyof GetV1BusinessPartnerCurrentChangeResponses];

export type PutV1BusinessPartnerCurrentChangeData = {
    /**
     * TODO
     */
    body: BusinessPartnerChangeBody;
    headers?: {
        'If-Match'?: string;
    };
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/change';
};

export type PutV1BusinessPartnerCurrentChangeErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Conflict
     */
    409: unknown;
    /**
     * Precondition Failed
     */
    412: unknown;
};

export type PutV1BusinessPartnerCurrentChangeResponses = {
    /**
     * Accepted
     */
    202: unknown;
};

export type GetV1BusinessPartnerCurrentDivisionsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/divisions';
};

export type GetV1BusinessPartnerCurrentDivisionsErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1BusinessPartnerCurrentDivisionsResponses = {
    /**
     * Successful Response
     */
    200: BusinessPartnerListBody;
};

export type GetV1BusinessPartnerCurrentDivisionsResponse = GetV1BusinessPartnerCurrentDivisionsResponses[keyof GetV1BusinessPartnerCurrentDivisionsResponses];

export type GetV1BusinessPartnerCurrentMigrationCandidateUserByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/businessPartner/current/migrationCandidateUser/{id}';
};

export type GetV1BusinessPartnerCurrentMigrationCandidateUserByIdErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1BusinessPartnerCurrentMigrationCandidateUserByIdResponses = {
    /**
     * Successful Response
     */
    200: MigrationCandidateUserBody;
};

export type GetV1BusinessPartnerCurrentMigrationCandidateUserByIdResponse = GetV1BusinessPartnerCurrentMigrationCandidateUserByIdResponses[keyof GetV1BusinessPartnerCurrentMigrationCandidateUserByIdResponses];

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/businessPartner/current/migrationCandidateUser/{id}/accept';
};

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptResponse = PostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptResponses[keyof PostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptResponses];

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/businessPartner/current/migrationCandidateUser/{id}/reject';
};

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectResponse = PostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectResponses[keyof PostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectResponses];

export type GetV1BusinessPartnerCurrentMigrationCandidateUsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/migrationCandidateUsers';
};

export type GetV1BusinessPartnerCurrentMigrationCandidateUsersErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1BusinessPartnerCurrentMigrationCandidateUsersResponses = {
    /**
     * Successful Response
     */
    200: MigrationCandidateUserListBody;
};

export type GetV1BusinessPartnerCurrentMigrationCandidateUsersResponse = GetV1BusinessPartnerCurrentMigrationCandidateUsersResponses[keyof GetV1BusinessPartnerCurrentMigrationCandidateUsersResponses];

export type DeleteV1BusinessPartnerCurrentRequestChangeOwnershipData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/requestChangeOwnership';
};

export type DeleteV1BusinessPartnerCurrentRequestChangeOwnershipErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type DeleteV1BusinessPartnerCurrentRequestChangeOwnershipResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteV1BusinessPartnerCurrentRequestChangeOwnershipResponse = DeleteV1BusinessPartnerCurrentRequestChangeOwnershipResponses[keyof DeleteV1BusinessPartnerCurrentRequestChangeOwnershipResponses];

export type GetV1BusinessPartnerCurrentRequestChangeOwnershipData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/requestChangeOwnership';
};

export type GetV1BusinessPartnerCurrentRequestChangeOwnershipErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type GetV1BusinessPartnerCurrentRequestChangeOwnershipResponses = {
    /**
     * Successful Response
     */
    200: ChangeOwnershipRequestBody;
};

export type GetV1BusinessPartnerCurrentRequestChangeOwnershipResponse = GetV1BusinessPartnerCurrentRequestChangeOwnershipResponses[keyof GetV1BusinessPartnerCurrentRequestChangeOwnershipResponses];

export type PostV1BusinessPartnerCurrentRequestChangeOwnershipData = {
    /**
     * TODO
     */
    body: ChangeOwnershipRequestPostBody;
    path?: never;
    query?: never;
    url: '/v1/businessPartner/current/requestChangeOwnership';
};

export type PostV1BusinessPartnerCurrentRequestChangeOwnershipErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1BusinessPartnerCurrentRequestChangeOwnershipResponses = {
    /**
     * Created
     */
    201: ChangeOwnershipRequestBody;
};

export type PostV1BusinessPartnerCurrentRequestChangeOwnershipResponse = PostV1BusinessPartnerCurrentRequestChangeOwnershipResponses[keyof PostV1BusinessPartnerCurrentRequestChangeOwnershipResponses];

export type GetV1BusinessPartnersData = {
    body?: never;
    path?: never;
    query?: {
        page?: number;
        limit?: number;
        /**
         * **example query**: *?filter[id][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[id]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[extId][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[extId]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[parentId][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[parentId]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[ownerId][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[ownerId]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[type][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[type]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[createdAt][operator]=value*<br><br> **valid operators:** *gt*, *gte*, *lt*, *lte*
         */
        'filter[createdAt]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[modifiedAt][operator]=value*<br><br> **valid operators:** *gt*, *gte*, *lt*, *lte*
         */
        'filter[modifiedAt]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[name][operator]=value*<br><br> **valid operators:** *startswith*
         */
        'filter[name]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.addressLineOne][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.addressLineOne]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.addressLineTwo][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.addressLineTwo]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.city][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.city]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.stateProvinceRegion][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.stateProvinceRegion]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.zipPostalCode][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.zipPostalCode]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.country][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.country]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query:** *?sort[field]=value*<br><br>**valid values:** *asc* *desc*<br><br>**valid fields:** *id* *extId* *parentId* *ownerId* *type* *createdAt* *modifiedAt* *name* *companyAddress.addressLineOne* *companyAddress.addressLineTwo* *companyAddress.city* *companyAddress.stateProvinceRegion* *companyAddress.zipPostalCode* *companyAddress.country*
         */
        sort?: {
            [key: string]: unknown;
        };
        search?: string;
    };
    url: '/v1/businessPartners';
};

export type GetV1BusinessPartnersErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1BusinessPartnersResponses = {
    /**
     * Successful Response
     */
    200: PaginatedBusinessPartnerListBody;
};

export type GetV1BusinessPartnersResponse = GetV1BusinessPartnersResponses[keyof GetV1BusinessPartnersResponses];

export type GetV1CustomerByCustomerNumberData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        customerNumber: string;
    };
    query?: never;
    url: '/v1/customer/{customerNumber}';
};

export type GetV1CustomerByCustomerNumberErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1CustomerByCustomerNumberResponses = {
    /**
     * Successful Response
     */
    200: CustomerBody;
};

export type GetV1CustomerByCustomerNumberResponse = GetV1CustomerByCustomerNumberResponses[keyof GetV1CustomerByCustomerNumberResponses];

export type GetV1CustomersData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * **example query**: *?filter[name][operator]=value*<br><br> **valid operators:** *startswith*
         */
        'filter[name]'?: {
            [key: string]: unknown;
        };
    };
    url: '/v1/customers';
};

export type GetV1CustomersErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1CustomersResponses = {
    /**
     * Successful Response
     */
    200: CustomerListBody;
};

export type GetV1CustomersResponse = GetV1CustomersResponses[keyof GetV1CustomersResponses];

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/identity/current/migrationCandidateBusinessPartner/{id}';
};

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdResponses = {
    /**
     * Successful Response
     */
    200: MigrationCandidateBusinessPartnerBody;
};

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdResponse = GetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdResponses[keyof GetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdResponses];

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptData = {
    /**
     * TODO
     */
    body: MigrationCandidateAcceptPostBody;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/identity/current/migrationCandidateBusinessPartner/{id}/accept';
};

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptResponses = {
    /**
     * Successful Response
     */
    200: MigrationCandidateBusinessPartnerBody;
};

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptResponse = PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptResponses[keyof PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptResponses];

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/identity/current/migrationCandidateBusinessPartner/{id}/reject';
};

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectResponse = PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectResponses[keyof PostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectResponses];

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/identity/current/migrationCandidateBusinessPartners';
};

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnersErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnersResponses = {
    /**
     * Successful Response
     */
    200: MigrationCandidateBusinessPartnerListBody;
};

export type GetV1IdentityCurrentMigrationCandidateBusinessPartnersResponse = GetV1IdentityCurrentMigrationCandidateBusinessPartnersResponses[keyof GetV1IdentityCurrentMigrationCandidateBusinessPartnersResponses];

export type PostV1IdentityCurrentRequestBusinessPartnerData = {
    /**
     * TODO
     */
    body: RequestBusinessPartnerEndCustomerPostBody;
    path?: never;
    query?: never;
    url: '/v1/identity/current/requestBusinessPartner';
};

export type PostV1IdentityCurrentRequestBusinessPartnerErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1IdentityCurrentRequestBusinessPartnerResponses = {
    /**
     * Accepted
     */
    202: unknown;
};

export type DeleteV1IdentityCurrentRequestBusinessPartnerByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/identity/current/requestBusinessPartner/{id}';
};

export type DeleteV1IdentityCurrentRequestBusinessPartnerByIdErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type DeleteV1IdentityCurrentRequestBusinessPartnerByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteV1IdentityCurrentRequestBusinessPartnerByIdResponse = DeleteV1IdentityCurrentRequestBusinessPartnerByIdResponses[keyof DeleteV1IdentityCurrentRequestBusinessPartnerByIdResponses];

export type GetV1IdentityCurrentRequestBusinessPartnerByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/identity/current/requestBusinessPartner/{id}';
};

export type GetV1IdentityCurrentRequestBusinessPartnerByIdErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1IdentityCurrentRequestBusinessPartnerByIdResponses = {
    /**
     * Successful Response
     */
    200: RequestBusinessPartnerBody;
};

export type GetV1IdentityCurrentRequestBusinessPartnerByIdResponse = GetV1IdentityCurrentRequestBusinessPartnerByIdResponses[keyof GetV1IdentityCurrentRequestBusinessPartnerByIdResponses];

export type PutV1IdentityCurrentRequestBusinessPartnerByIdData = {
    /**
     * TODO
     */
    body: RequestBusinessPartnerPatchBody;
    headers?: {
        'If-Match'?: string;
    };
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/identity/current/requestBusinessPartner/{id}';
};

export type PutV1IdentityCurrentRequestBusinessPartnerByIdErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
    /**
     * Precondition Failed
     */
    412: unknown;
};

export type PutV1IdentityCurrentRequestBusinessPartnerByIdResponses = {
    /**
     * Successful Response
     */
    200: RequestBusinessPartnerBody;
    /**
     * Accepted
     */
    202: unknown;
};

export type PutV1IdentityCurrentRequestBusinessPartnerByIdResponse = PutV1IdentityCurrentRequestBusinessPartnerByIdResponses[keyof PutV1IdentityCurrentRequestBusinessPartnerByIdResponses];

export type GetV1IdentityCurrentRequestBusinessPartnersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/identity/current/requestBusinessPartners';
};

export type GetV1IdentityCurrentRequestBusinessPartnersErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1IdentityCurrentRequestBusinessPartnersResponses = {
    /**
     * Successful Response
     */
    200: RequestBusinessPartnerListBody;
};

export type GetV1IdentityCurrentRequestBusinessPartnersResponse = GetV1IdentityCurrentRequestBusinessPartnersResponses[keyof GetV1IdentityCurrentRequestBusinessPartnersResponses];

export type GetV1MigrationCandidateBusinessPartnerByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/migrationCandidateBusinessPartner/{id}';
};

export type GetV1MigrationCandidateBusinessPartnerByIdErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1MigrationCandidateBusinessPartnerByIdResponses = {
    /**
     * Successful Response
     */
    200: MigrationCandidateBusinessPartnerBody;
};

export type GetV1MigrationCandidateBusinessPartnerByIdResponse = GetV1MigrationCandidateBusinessPartnerByIdResponses[keyof GetV1MigrationCandidateBusinessPartnerByIdResponses];

export type PostV1MigrationCandidateBusinessPartnerByIdAcceptData = {
    /**
     * TODO
     */
    body: MigrationCandidateAcceptPostBody;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/migrationCandidateBusinessPartner/{id}/accept';
};

export type PostV1MigrationCandidateBusinessPartnerByIdAcceptErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1MigrationCandidateBusinessPartnerByIdAcceptResponses = {
    /**
     * Successful Response
     */
    200: MigrationCandidateBusinessPartnerBody;
};

export type PostV1MigrationCandidateBusinessPartnerByIdAcceptResponse = PostV1MigrationCandidateBusinessPartnerByIdAcceptResponses[keyof PostV1MigrationCandidateBusinessPartnerByIdAcceptResponses];

export type PostV1MigrationCandidateBusinessPartnerByIdRejectData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/migrationCandidateBusinessPartner/{id}/reject';
};

export type PostV1MigrationCandidateBusinessPartnerByIdRejectErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type PostV1MigrationCandidateBusinessPartnerByIdRejectResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1MigrationCandidateBusinessPartnerByIdRejectResponse = PostV1MigrationCandidateBusinessPartnerByIdRejectResponses[keyof PostV1MigrationCandidateBusinessPartnerByIdRejectResponses];

export type GetV1MigrationCandidateBusinessPartnersData = {
    body?: never;
    path?: never;
    query?: {
        page?: number;
        limit?: number;
        /**
         * **example query**: *?filter[name][operator]=value*<br><br> **valid operators:** *startswith*
         */
        'filter[name]'?: {
            [key: string]: unknown;
        };
    };
    url: '/v1/migrationCandidateBusinessPartners';
};

export type GetV1MigrationCandidateBusinessPartnersErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1MigrationCandidateBusinessPartnersResponses = {
    /**
     * Successful Response
     */
    200: PaginatedMigrationCandidateBusinessPartnerListBody;
};

export type GetV1MigrationCandidateBusinessPartnersResponse = GetV1MigrationCandidateBusinessPartnersResponses[keyof GetV1MigrationCandidateBusinessPartnersResponses];

export type GetV1ProductLinesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/productLines';
};

export type GetV1ProductLinesErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1ProductLinesResponses = {
    /**
     * Successful Response
     */
    200: ProductLineListBody;
};

export type GetV1ProductLinesResponse = GetV1ProductLinesResponses[keyof GetV1ProductLinesResponses];

export type GetV1ProvidersData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * **example query**: *?filter[name][operator]=value*<br><br> **valid operators:** *startswith*
         */
        'filter[name]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.addressLineOne][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.addressLineOne]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.addressLineTwo][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.addressLineTwo]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.city][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.city]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.stateProvinceRegion][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.stateProvinceRegion]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.zipPostalCode][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.zipPostalCode]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[companyAddress.country][operator]=value*<br><br> **valid operators:** *eq*
         */
        'filter[companyAddress.country]'?: {
            [key: string]: unknown;
        };
        page?: number;
        limit?: number;
    };
    url: '/v1/providers';
};

export type GetV1ProvidersErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1ProvidersResponses = {
    /**
     * Successful Response
     */
    200: ProviderListBody;
};

export type GetV1ProvidersResponse = GetV1ProvidersResponses[keyof GetV1ProvidersResponses];

export type PostV1RequestBusinessPartnerDivisionData = {
    /**
     * TODO
     */
    body: RequestBusinessPartnerDivisionPostBody;
    path?: never;
    query?: never;
    url: '/v1/requestBusinessPartner/division';
};

export type PostV1RequestBusinessPartnerDivisionErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1RequestBusinessPartnerDivisionResponses = {
    /**
     * Accepted
     */
    202: unknown;
};

export type GetV1RequestBusinessPartnerDivisionsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/requestBusinessPartner/divisions';
};

export type GetV1RequestBusinessPartnerDivisionsErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1RequestBusinessPartnerDivisionsResponses = {
    /**
     * Successful Response
     */
    200: RequestBusinessPartnerListBody;
};

export type GetV1RequestBusinessPartnerDivisionsResponse = GetV1RequestBusinessPartnerDivisionsResponses[keyof GetV1RequestBusinessPartnerDivisionsResponses];

export type GetV1RequestBusinessPartnerProxiesData = {
    body?: never;
    path?: never;
    query?: {
        page?: number;
        limit?: number;
        /**
         * **example query**: *?filter[name][operator]=value*<br><br> **valid operators:** *startswith*
         */
        'filter[name]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[status][operator]=value*<br><br> **valid operators:** *in*
         */
        'filter[status]'?: {
            [key: string]: unknown;
        };
    };
    url: '/v1/requestBusinessPartner/proxies';
};

export type GetV1RequestBusinessPartnerProxiesErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1RequestBusinessPartnerProxiesResponses = {
    /**
     * Successful Response
     */
    200: PaginatedRequestBusinessPartnerListBody;
};

export type GetV1RequestBusinessPartnerProxiesResponse = GetV1RequestBusinessPartnerProxiesResponses[keyof GetV1RequestBusinessPartnerProxiesResponses];

export type PostV1RequestBusinessPartnerProxyData = {
    /**
     * TODO
     */
    body: RequestBusinessPartnerProxyPostBody;
    path?: never;
    query?: never;
    url: '/v1/requestBusinessPartner/proxy';
};

export type PostV1RequestBusinessPartnerProxyErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1RequestBusinessPartnerProxyResponses = {
    /**
     * Created
     */
    201: RequestBusinessPartnerBody;
    /**
     * Accepted
     */
    202: unknown;
};

export type PostV1RequestBusinessPartnerProxyResponse = PostV1RequestBusinessPartnerProxyResponses[keyof PostV1RequestBusinessPartnerProxyResponses];

export type DeleteV1RequestBusinessPartnerByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestBusinessPartner/{id}';
};

export type DeleteV1RequestBusinessPartnerByIdErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type DeleteV1RequestBusinessPartnerByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteV1RequestBusinessPartnerByIdResponse = DeleteV1RequestBusinessPartnerByIdResponses[keyof DeleteV1RequestBusinessPartnerByIdResponses];

export type GetV1RequestBusinessPartnerByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestBusinessPartner/{id}';
};

export type GetV1RequestBusinessPartnerByIdErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1RequestBusinessPartnerByIdResponses = {
    /**
     * Successful Response
     */
    200: RequestBusinessPartnerBody;
};

export type GetV1RequestBusinessPartnerByIdResponse = GetV1RequestBusinessPartnerByIdResponses[keyof GetV1RequestBusinessPartnerByIdResponses];

export type PutV1RequestBusinessPartnerByIdData = {
    /**
     * TODO
     */
    body: RequestBusinessPartnerPatchBody;
    headers?: {
        'If-Match'?: string;
    };
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestBusinessPartner/{id}';
};

export type PutV1RequestBusinessPartnerByIdErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type PutV1RequestBusinessPartnerByIdResponses = {
    /**
     * Successful Response
     */
    200: RequestBusinessPartnerBody;
    /**
     * Accepted
     */
    202: unknown;
};

export type PutV1RequestBusinessPartnerByIdResponse = PutV1RequestBusinessPartnerByIdResponses[keyof PutV1RequestBusinessPartnerByIdResponses];

export type PostV1RequestBusinessPartnerByIdAcceptData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestBusinessPartner/{id}/accept';
};

export type PostV1RequestBusinessPartnerByIdAcceptErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1RequestBusinessPartnerByIdAcceptResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1RequestBusinessPartnerByIdAcceptResponse = PostV1RequestBusinessPartnerByIdAcceptResponses[keyof PostV1RequestBusinessPartnerByIdAcceptResponses];

export type PostV1RequestBusinessPartnerByIdDeclineData = {
    /**
     * TODO
     */
    body: RequestBusinessPartnerDeclinePostBody;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestBusinessPartner/{id}/decline';
};

export type PostV1RequestBusinessPartnerByIdDeclineErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1RequestBusinessPartnerByIdDeclineResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1RequestBusinessPartnerByIdDeclineResponse = PostV1RequestBusinessPartnerByIdDeclineResponses[keyof PostV1RequestBusinessPartnerByIdDeclineResponses];

export type PostV1RequestBusinessPartnerByIdRegistrationTokenData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestBusinessPartner/{id}/registrationToken';
};

export type PostV1RequestBusinessPartnerByIdRegistrationTokenErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type PostV1RequestBusinessPartnerByIdRegistrationTokenResponses = {
    /**
     * Successful Response
     */
    200: RegistrationTokenBody;
};

export type PostV1RequestBusinessPartnerByIdRegistrationTokenResponse = PostV1RequestBusinessPartnerByIdRegistrationTokenResponses[keyof PostV1RequestBusinessPartnerByIdRegistrationTokenResponses];

export type GetV1RequestChangeOwnershipByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestChangeOwnership/{id}';
};

export type GetV1RequestChangeOwnershipByIdErrors = {
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type GetV1RequestChangeOwnershipByIdResponses = {
    /**
     * Successful Response
     */
    200: ChangeOwnershipRequestAcceptBody;
};

export type GetV1RequestChangeOwnershipByIdResponse = GetV1RequestChangeOwnershipByIdResponses[keyof GetV1RequestChangeOwnershipByIdResponses];

export type PostV1RequestChangeOwnershipByIdAcceptData = {
    body?: never;
    headers?: {
        'If-Match'?: string;
    };
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/requestChangeOwnership/{id}/accept';
};

export type PostV1RequestChangeOwnershipByIdAcceptErrors = {
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1RequestChangeOwnershipByIdAcceptResponses = {
    /**
     * Accepted
     */
    202: unknown;
};

export type GetV1RolesCurrentData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/v1/roles/current';
};

export type GetV1RolesCurrentErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1RolesCurrentResponses = {
    /**
     * Successful Response
     */
    200: RoleListBody;
};

export type GetV1RolesCurrentResponse = GetV1RolesCurrentResponses[keyof GetV1RolesCurrentResponses];

export type PostV1ServiceAgreementWithCustomerData = {
    /**
     * TODO
     */
    body: ServiceAgreementWithCustomerPostBody;
    path?: never;
    query?: never;
    url: '/v1/serviceAgreement/withCustomer';
};

export type PostV1ServiceAgreementWithCustomerErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1ServiceAgreementWithCustomerResponses = {
    /**
     * Created
     */
    201: ServiceAgreementBody;
};

export type PostV1ServiceAgreementWithCustomerResponse = PostV1ServiceAgreementWithCustomerResponses[keyof PostV1ServiceAgreementWithCustomerResponses];

export type PostV1ServiceAgreementWithProviderData = {
    /**
     * TODO
     */
    body: ServiceAgreementWithProviderPostBody;
    path?: never;
    query?: never;
    url: '/v1/serviceAgreement/withProvider';
};

export type PostV1ServiceAgreementWithProviderErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1ServiceAgreementWithProviderResponses = {
    /**
     * Created
     */
    201: ServiceAgreementBody;
};

export type PostV1ServiceAgreementWithProviderResponse = PostV1ServiceAgreementWithProviderResponses[keyof PostV1ServiceAgreementWithProviderResponses];

export type DeleteV1ServiceAgreementByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/serviceAgreement/{id}';
};

export type DeleteV1ServiceAgreementByIdErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type DeleteV1ServiceAgreementByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteV1ServiceAgreementByIdResponse = DeleteV1ServiceAgreementByIdResponses[keyof DeleteV1ServiceAgreementByIdResponses];

export type GetV1ServiceAgreementByIdData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/serviceAgreement/{id}';
};

export type GetV1ServiceAgreementByIdErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type GetV1ServiceAgreementByIdResponses = {
    /**
     * Successful Response
     */
    200: ServiceAgreementBody;
};

export type GetV1ServiceAgreementByIdResponse = GetV1ServiceAgreementByIdResponses[keyof GetV1ServiceAgreementByIdResponses];

export type PutV1ServiceAgreementByIdData = {
    /**
     * TODO
     */
    body: ServiceAgreementPatchBody;
    headers?: {
        'If-Match'?: string;
    };
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/serviceAgreement/{id}';
};

export type PutV1ServiceAgreementByIdErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
};

export type PutV1ServiceAgreementByIdResponses = {
    /**
     * Successful Response
     */
    200: ServiceAgreementBody;
};

export type PutV1ServiceAgreementByIdResponse = PutV1ServiceAgreementByIdResponses[keyof PutV1ServiceAgreementByIdResponses];

export type PostV1ServiceAgreementByIdAcceptData = {
    body?: never;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/serviceAgreement/{id}/accept';
};

export type PostV1ServiceAgreementByIdAcceptErrors = {
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1ServiceAgreementByIdAcceptResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1ServiceAgreementByIdAcceptResponse = PostV1ServiceAgreementByIdAcceptResponses[keyof PostV1ServiceAgreementByIdAcceptResponses];

export type PostV1ServiceAgreementByIdDeclineData = {
    /**
     * TODO
     */
    body: ServiceAgreementDeclinePostBody;
    path: {
        /**
         * TODO
         */
        id: string;
    };
    query?: never;
    url: '/v1/serviceAgreement/{id}/decline';
};

export type PostV1ServiceAgreementByIdDeclineErrors = {
    /**
     * Bad Request
     */
    400: unknown;
    /**
     * Forbidden
     */
    403: unknown;
    /**
     * Not found
     */
    404: unknown;
    /**
     * Conflict
     */
    409: unknown;
};

export type PostV1ServiceAgreementByIdDeclineResponses = {
    /**
     * No Content
     */
    204: void;
};

export type PostV1ServiceAgreementByIdDeclineResponse = PostV1ServiceAgreementByIdDeclineResponses[keyof PostV1ServiceAgreementByIdDeclineResponses];

export type GetV1ServiceAgreementsWithCustomerData = {
    body?: never;
    path?: never;
    query?: {
        page?: number;
        limit?: number;
        /**
         * **example query**: *?filter[providerBusinessPartnerId][operator]=value*<br><br> **valid operators:** *in*
         */
        'filter[providerBusinessPartnerId]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[customer.name][operator]=value*<br><br> **valid operators:** *startswith*
         */
        'filter[customer.name]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[serviceAgreementStatus][operator]=value*<br><br> **valid operators:** *in*
         */
        'filter[serviceAgreementStatus]'?: {
            [key: string]: unknown;
        };
        scope?: string;
    };
    url: '/v1/serviceAgreements/withCustomer';
};

export type GetV1ServiceAgreementsWithCustomerErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1ServiceAgreementsWithCustomerResponses = {
    /**
     * Successful Response
     */
    200: PaginatedServiceAgreementListBody;
};

export type GetV1ServiceAgreementsWithCustomerResponse = GetV1ServiceAgreementsWithCustomerResponses[keyof GetV1ServiceAgreementsWithCustomerResponses];

export type GetV1ServiceAgreementsWithProviderData = {
    body?: never;
    path?: never;
    query?: {
        page?: number;
        limit?: number;
        /**
         * **example query**: *?filter[providerBusinessPartnerId][operator]=value*<br><br> **valid operators:** *in*
         */
        'filter[providerBusinessPartnerId]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[customer.name][operator]=value*<br><br> **valid operators:** *startswith*
         */
        'filter[customer.name]'?: {
            [key: string]: unknown;
        };
        /**
         * **example query**: *?filter[serviceAgreementStatus][operator]=value*<br><br> **valid operators:** *in*
         */
        'filter[serviceAgreementStatus]'?: {
            [key: string]: unknown;
        };
    };
    url: '/v1/serviceAgreements/withProvider';
};

export type GetV1ServiceAgreementsWithProviderErrors = {
    /**
     * Forbidden
     */
    403: unknown;
};

export type GetV1ServiceAgreementsWithProviderResponses = {
    /**
     * Successful Response
     */
    200: PaginatedServiceAgreementListBody;
};

export type GetV1ServiceAgreementsWithProviderResponse = GetV1ServiceAgreementsWithProviderResponses[keyof GetV1ServiceAgreementsWithProviderResponses];
